import { useState, useEffect } from 'react';
import { UserForm } from './components/UserForm';
import { LoginPage } from './components/LoginPage';
import { TaskSelection } from './components/TaskSelection';
import { AdminDashboard } from './components/NewAdminDashboard';
import { CategorySelection } from './components/CategorySelection';
import { ErrorBoundary } from './components/ErrorBoundary';
import { EmployeeTimeTrackingScreen } from './components/EmployeeTimeTrackingScreen';
import { OperatorHomeScreen } from './components/OperatorHomeScreen';
import { SupervisorHomeScreen } from './components/SupervisorHomeScreen';
import { TimesheetsScreen } from './components/admin/time-tracking/TimesheetsScreen';
import type { Operator } from './types/timeTracking';
import type { Project } from './types/project';
import type { TimeEntry } from './types/timeTracking';
import * as api from './utils/api';
import { supabase } from './utils/supabase/client';

export interface Category {
  id: string;
  name: string;
  allowedVendors?: string[];
  allowedPINs?: string[];
}

export interface Task {
  id: string;
  title: string;
  category: 'mechanical' | 'civil';
  categoryId: string;
  questions: Question[];
  assignedTo?: {
    operators?: boolean; // Gama Operators
    vendors?: string[]; // Array of vendor IDs
    supervisors?: boolean | string[]; // Array of supervisor account IDs, or true for legacy/all-access
  };
}

export interface Question {
  id: string;
  type: 'multiple-choice' | 'yes-no' | 'number' | 'dropdown' | 'checkbox' | 'text';
  question: string;
  options?: string[];
  required?: boolean;
  conditionalOn?: {
    questionId: string;
    value: string;
  };
}

export interface TaskSubmission {
  id: string;
  projectId: string | null;
  taskId: string;
  date: string; // YYYY-MM-DD
  responses: Record<string, string | number | boolean>;
  submittedBy: string;
  submittedAt: string;
  notes?: string;
}

// Default categories
const DEFAULT_CATEGORIES: Category[] = [
  { id: 'compressor', name: 'Compressor' },
  { id: 'ctb-sat', name: 'CTB & SAT' },
];

// Default tasks with basic questions
const DEFAULT_TASKS: Task[] = [
  { id: 'mob', title: 'MOB', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Describe the mobilization activities' }] },
  { id: 'layout', title: 'Layout', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'text', question: 'Provide layout details' }] },
  { id: 'hydrovavc', title: 'Hydrovavc', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Enter hydrovavc information' }] },
  { id: 'ditch', title: 'Ditch', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'number', question: 'Length of ditch (meters)' }] },
  { id: 'string-pipe', title: 'String Pipe', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Describe string pipe work' }] },
  { id: 'fabrication', title: 'Fabrication', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Fabrication details' }] },
  { id: 'fab-install', title: 'Fab Install', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Installation details' }] },
  { id: 'weld-fuse-pipe', title: 'Weld/Fuse Pipe', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Welding/Fusing details' }] },
  { id: 'coating', title: 'Coating', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Coating information' }] },
  { id: 'jeeping', title: 'Jeeping', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'text', question: 'Jeeping details' }] },
  { id: 'set-foundations', title: 'Set Foundations', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'text', question: 'Foundation work description' }] },
  { id: 'tie-ins', title: 'Tie Ins', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Tie-in details' }] },
  { id: 'structural-welding', title: 'Structural Welding', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Welding information' }] },
  { id: 'structural-install', title: 'Structural Install', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'text', question: 'Installation details' }] },
  { id: 'lower-in', title: 'Lower In', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Lower-in procedure details' }] },
  { id: 'backfill', title: 'Backfill', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'number', question: 'Backfill volume (cubic meters)' }] },
  { id: 'hydrotest', title: 'Hydrotest', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'yes-no', question: 'Was hydrotest successful?' }] },
  { id: 'final-bolt-up', title: 'Final Bolt up', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Final bolt-up details' }] },
  { id: 'clean-up', title: 'Clean up', category: 'civil', categoryId: 'ctb-sat', questions: [{ id: 'q1', type: 'text', question: 'Cleanup activities' }] },
  { id: 'demob', title: 'Demob', category: 'mechanical', categoryId: 'compressor', questions: [{ id: 'q1', type: 'text', question: 'Demobilization activities' }] },
];

// Initial demo project — includes tasks with number & yes-no questions for timeline demo
const INITIAL_PROJECT: Project = {
  id: 'proj-1',
  name: 'Gama LLC – CTB & SAT',
  number: 'P-2024-001',
  dateCreated: '2024-01-15',
  status: 'active',
  categoryIds: ['compressor', 'ctb-sat'],
  taskIds: ['ditch', 'backfill', 'hydrotest', 'mob', 'layout'],
  tasks: [
    { 
      id: 'ditch', 
      title: 'Ditch', 
      category: 'civil', 
      categoryId: 'ctb-sat', 
      questions: [{ id: 'q1', type: 'number', question: 'Length of ditch (meters)', goal: 500, currentProgress: 0 }],
      selectedQuestionIds: ['q1']
    },
    { 
      id: 'backfill', 
      title: 'Backfill', 
      category: 'civil', 
      categoryId: 'ctb-sat', 
      questions: [{ id: 'q1', type: 'number', question: 'Backfill volume (cubic meters)', goal: 300, currentProgress: 0 }],
      selectedQuestionIds: ['q1']
    },
    { 
      id: 'hydrotest', 
      title: 'Hydrotest', 
      category: 'mechanical', 
      categoryId: 'compressor', 
      questions: [{ id: 'q1', type: 'yes-no', question: 'Was hydrotest successful?' }],
      selectedQuestionIds: ['q1']
    },
    { 
      id: 'mob', 
      title: 'MOB', 
      category: 'mechanical', 
      categoryId: 'compressor', 
      questions: [{ id: 'q1', type: 'text', question: 'Describe the mobilization activities' }],
      selectedQuestionIds: ['q1']
    },
    { 
      id: 'layout', 
      title: 'Layout', 
      category: 'civil', 
      categoryId: 'ctb-sat', 
      questions: [{ id: 'q1', type: 'text', question: 'Provide layout details' }],
      selectedQuestionIds: ['q1']
    },
  ],
};

const TIME_ENTRIES_STORAGE_KEY = 'timeEntries';
const OPERATORS_STORAGE_KEY = 'operators';

const readLocalTimeEntries = (): TimeEntry[] => {
  try {
    const stored = localStorage.getItem(TIME_ENTRIES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const writeLocalTimeEntries = (timeEntries: TimeEntry[]) => {
  localStorage.setItem(TIME_ENTRIES_STORAGE_KEY, JSON.stringify(timeEntries));
};

const readLocalOperators = (): Operator[] => {
  try {
    const stored = localStorage.getItem(OPERATORS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export default function App() {
  const getLocalDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const syncProjectWithTaskDefinitions = (project: Project, taskLibrary: Task[]): Project => {
    const syncedTasks = project.tasks.map((projectTask) => {
      const sourceTask = taskLibrary.find(task => task.id === projectTask.id);
      if (!sourceTask) return projectTask;

      const syncedQuestions = sourceTask.questions.map((sourceQuestion) => {
        const existingQuestion = projectTask.questions.find(question => question.id === sourceQuestion.id);
        if (!existingQuestion) {
          return {
            ...sourceQuestion,
            goal: sourceQuestion.type === 'number' ? 0 : undefined,
            currentProgress: 0,
          };
        }

        return {
          ...sourceQuestion,
          goal: existingQuestion.goal,
          currentProgress: existingQuestion.currentProgress,
        };
      });

      return {
        ...projectTask,
        ...sourceTask,
        questions: syncedQuestions,
        selectedQuestionIds: projectTask.selectedQuestionIds,
      };
    });

    return {
      ...project,
      tasks: syncedTasks,
      taskIds: syncedTasks.map(task => task.id),
    };
  };

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userScreen, setUserScreen] = useState<'home' | 'tasks' | 'clock' | 'timesheets' | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<string | null>(null); // Changed to string - stores task ID
  const [completedTasks, setCompletedTasks] = useState<string[]>([]);
  const [userRole, setUserRole] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userRoleType, setUserRoleType] = useState<'operator' | 'vendor' | 'supervisor' | 'microsoft' | 'employee' | null>(null);
  const [userVendorId, setUserVendorId] = useState<string | null>(null);
  const [userPersonName, setUserPersonName] = useState<string | null>(null);
  const [userEmployeeId, setUserEmployeeId] = useState<string | null>(null);
  const [accessibleProjects, setAccessibleProjects] = useState<string[]>([]);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [operators, setOperators] = useState<Operator[]>(() => readLocalOperators());
  const [projects, setProjects] = useState<Project[]>([INITIAL_PROJECT]);
  const [allTasks, setAllTasks] = useState<Task[]>(DEFAULT_TASKS);
  const [categories, setCategories] = useState<Category[]>(DEFAULT_CATEGORIES);
  const syncedProjects = projects.map(project => syncProjectWithTaskDefinitions(project, allTasks));

  // Load data from backend on mount
  useEffect(() => {
    loadDataFromBackend();
  }, []);

  const loadDataFromBackend = async () => {
    try {
      // Try to load existing data
      const [loadedCategories, loadedTasks, loadedProjects, loadedSubmissions, loadedTimeEntries] = await Promise.all([
        api.getCategories(),
        api.getTasks(),
        api.getProjects(),
        api.getSubmissions(),
        api.getTimeEntries(),
      ]);

      // If database is empty, initialize with defaults
      if (loadedCategories.length === 0 && loadedTasks.length === 0 && loadedProjects.length === 0) {
        console.log('Initializing database with default data...');
        await api.initializeDatabase({
          categories: DEFAULT_CATEGORIES,
          tasks: DEFAULT_TASKS,
          projects: [INITIAL_PROJECT],
        });
        
        // Reload after initialization
        const [newCategories, newTasks, newProjects] = await Promise.all([
          api.getCategories(),
          api.getTasks(),
          api.getProjects(),
        ]);
        
        setCategories(newCategories.length > 0 ? newCategories : DEFAULT_CATEGORIES);
        setAllTasks(newTasks.length > 0 ? newTasks : DEFAULT_TASKS);
        setProjects(newProjects.length > 0 ? newProjects : [INITIAL_PROJECT]);
        setTimeEntries(readLocalTimeEntries());
      } else {
        // Use loaded data
        setCategories(loadedCategories.length > 0 ? loadedCategories : DEFAULT_CATEGORIES);
        setAllTasks(loadedTasks.length > 0 ? loadedTasks : DEFAULT_TASKS);
        setProjects(loadedProjects.length > 0 ? loadedProjects : [INITIAL_PROJECT]);
        setSubmissions(loadedSubmissions || []);
        setTimeEntries((loadedTimeEntries && loadedTimeEntries.length > 0) ? loadedTimeEntries : readLocalTimeEntries());
      }
    } catch (error) {
      console.error('Error loading data from backend:', error);
      // Fall back to defaults on error
      setCategories(DEFAULT_CATEGORIES);
      setAllTasks(DEFAULT_TASKS);
      setProjects([INITIAL_PROJECT]);
      setTimeEntries(readLocalTimeEntries());
    }
  };

  // Ensure there's always a valid activeProjectId when projects exist
  useEffect(() => {
    if (projects.length > 0) {
      const activeExists = projects.some(p => p.id === activeProjectId);
      if (!activeExists) {
        setActiveProjectId(projects[0].id);
      }
    }
  }, [projects, activeProjectId]);

  const handleTaskComplete = (taskId: string, responses?: Record<string, string | number | boolean>) => {
    if (!completedTasks.includes(taskId)) {
      setCompletedTasks([...completedTasks, taskId]);
    }
    // Save submission
    if (responses && activeProjectId) {
      const now = new Date();
      const dateKey = getLocalDateKey(now);
      
      // Build submittedBy string with person name and organization
      let submittedByText = userName;
      if (userPersonName) {
        submittedByText = `${userPersonName} (${userName})`;
      }
      
      const newSubmission: TaskSubmission = {
        id: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        projectId: activeProjectId,
        taskId,
        date: dateKey,
        responses,
        submittedBy: submittedByText,
        submittedAt: now.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
      };
      
      setSubmissions(prev => {
        // Allow multiple submissions for the same task+date+project - don't override
        return [...prev, newSubmission];
      });
      
      // Save to backend
      api.saveSubmission(newSubmission).catch(error => {
        console.error('Error saving submission to backend:', error);
      });
    }
    setSelectedTask(null);
  };

  const handleAdminLogin = () => {
    setIsLoggedIn(true);
    setIsAdmin(true);
    setUserRole('Admin');
    setCompletedTasks([]);
  };

  const refreshOperators = () => setOperators(readLocalOperators());

  const handleUserLogin = (pinCode?: string, roleName?: string, allowedProjects?: string[], roleType?: 'operator' | 'vendor' | 'supervisor' | 'microsoft' | 'employee', vendorId?: string, personName?: string, accountId?: string) => {
    setIsLoggedIn(true);
    setIsAdmin(false);
    setCompletedTasks([]);

    const finalRoleType = roleType || (vendorId === 'gama-operator' ? 'operator' : 'vendor');

    if ((roleType === 'operator' || roleType === 'employee') && roleName && allowedProjects) {
      setUserRole(roleName);
      setUserName(personName || roleName);
      setUserPersonName(personName || roleName);
      setAccessibleProjects(allowedProjects);
      setUserRoleType(roleType);
      setUserVendorId(null);
      setUserEmployeeId(accountId || null);
      if (roleType === 'operator') {
        setUserScreen('home');
      }
      if (allowedProjects.length > 0) {
        setActiveProjectId(allowedProjects[0]);
      }
    } else if (finalRoleType === 'microsoft') {
      const supervisorName = personName || 'Supervisor';
      const displayName = `${supervisorName} (Supervisor)`;
      setUserRole(displayName);
      setUserName(displayName);
      setUserPersonName(supervisorName);
      setAccessibleProjects(allowedProjects || []);
      setUserRoleType('microsoft');
      setUserVendorId(vendorId || null); // supervisor account ID for task filtering
      setUserEmployeeId(accountId || null); // needed for clock-in/out
      setUserScreen('home');
      if (allowedProjects && allowedProjects.length > 0) {
        setActiveProjectId(allowedProjects[0]);
      }
    } else if (pinCode && roleName && allowedProjects) {
      // PIN-based login (operator or vendor)
      setUserRole(roleName);
      setUserName(roleName);
      setAccessibleProjects(allowedProjects);
      setUserRoleType(finalRoleType);
      setUserVendorId(vendorId || null);
      setUserPersonName(personName || null);
      setUserEmployeeId(null);
      if (allowedProjects.length > 0) {
        setActiveProjectId(allowedProjects[0]);
      }
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('oauthLoginType');
    sessionStorage.removeItem('oauthAuthenticatedRole');
    void supabase.auth.signOut();
    setIsLoggedIn(false);
    setIsAdmin(false);
    setUserScreen(null);
    setSelectedCategory(null);
    setSelectedTask(null);
    setUserRole('');
    setAccessibleProjects([]);
    setUserRoleType(null);
    setUserVendorId(null);
    setUserPersonName(null);
    setUserEmployeeId(null);
    setCompletedTasks([]);
  };

  const handleUserLogout = () => {
    if (currentEmployeeTimeEntry) {
      const confirmed = window.confirm(
        'You are currently clocked in. Logging out will clock you out of your current project. Continue?'
      );
      if (!confirmed) return;
      handleEmployeeClockOut();
    }
    handleLogout();
  };

  const currentEmployeeTimeEntry = userEmployeeId
    ? (timeEntries.find(entry => entry.employeeId === userEmployeeId && entry.status === 'open') || null)
    : null;

  const handleEmployeeClockIn = () => {
    if (!userEmployeeId || !activeProjectId || !userRole) {
      alert('Please select a project before clocking in.');
      return;
    }

    if (currentEmployeeTimeEntry) {
      alert('You are already clocked in.');
      return;
    }

    const now = new Date();
    const newEntry: TimeEntry = {
      id: `time-entry-${Date.now()}`,
      employeeId: userEmployeeId,
      employeeName: userPersonName || userName,
      employeeEmail: userRole,
      projectId: activeProjectId,
      date: getLocalDateKey(now),
      clockInAt: now.toISOString(),
      clockOutAt: null,
      totalHours: null,
      status: 'open',
      roleType: userRoleType === 'microsoft' ? 'supervisor' : 'operator',
    };

    setTimeEntries(prev => {
      const updated = [...prev, newEntry];
      writeLocalTimeEntries(updated);
      return updated;
    });
    api.saveTimeEntry(newEntry).catch(error => {
      console.error('Error saving time entry:', error);
    });
  };

  const handleEmployeeClockOut = () => {
    if (!currentEmployeeTimeEntry) {
      alert('You are not currently clocked in.');
      return;
    }

    const now = new Date();
    const totalHours = (now.getTime() - new Date(currentEmployeeTimeEntry.clockInAt).getTime()) / 3600000;
    const updatedEntry: TimeEntry = {
      ...currentEmployeeTimeEntry,
      clockOutAt: now.toISOString(),
      totalHours,
      status: 'closed',
    };

    setTimeEntries(prev => {
      const updated = prev.map(entry => entry.id === updatedEntry.id ? updatedEntry : entry);
      writeLocalTimeEntries(updated);
      return updated;
    });
    api.saveTimeEntry(updatedEntry).catch(error => {
      console.error('Error updating time entry:', error);
    });
  };

  const handleAddTask = (task: Task) => {
    setAllTasks([...allTasks, task]);
    // Save to backend
    api.saveTask(task).catch(error => {
      console.error('Error saving task to backend:', error);
    });
  };
  
  const handleUpdateTask = (updatedTask: Task) => {
    setAllTasks(allTasks.map(t => t.id === updatedTask.id ? updatedTask : t));
    // Save to backend
    api.saveTask(updatedTask).catch(error => {
      console.error('Error updating task in backend:', error);
    });
  };
  
  const handleDeleteTask = (taskId: string) => {
    setAllTasks(allTasks.filter(t => t.id !== taskId));
    // Delete from backend
    api.deleteTask(taskId).catch(error => {
      console.error('Error deleting task from backend:', error);
    });
  };
  
  const handleAddCategory = (category: Category) => {
    const existingIndex = categories.findIndex(c => c.id === category.id);
    if (existingIndex >= 0) {
      setCategories(categories.map(c => c.id === category.id ? category : c));
    } else {
      setCategories([...categories, category]);
    }
    // Save to backend
    api.saveCategory(category).catch(error => {
      console.error('Error saving category to backend:', error);
    });
  };
  
  const handleAddProject = (project: Project) => {
    setProjects([...projects, project]);
    // Save to backend
    api.saveProject(project).catch(error => {
      console.error('Error saving project to backend:', error);
    });
  };
  
  const handleUpdateProject = (updated: Project) => {
    setProjects(projects.map(p => p.id === updated.id ? updated : p));
    // Save to backend
    api.saveProject(updated).catch(error => {
      console.error('Error updating project in backend:', error);
    });
  };
  
  const handleDeleteProject = (projectId: string) => {
    setProjects(projects.filter(p => p.id !== projectId));
    if (activeProjectId === projectId) {
      const remaining = projects.filter(p => p.id !== projectId);
      setActiveProjectId(remaining[0]?.id || '');
    }
    // Delete from backend
    api.deleteProject(projectId).catch(error => {
      console.error('Error deleting project from backend:', error);
    });
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedTask(null);
  };

  if (!isLoggedIn) {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <LoginPage
          onLogin={handleUserLogin}
          onAdminLogin={handleAdminLogin}
          categories={categories}
        />
      </ErrorBoundary>
    );
  }

  // Admin View
  if (isAdmin) {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <AdminDashboard
          tasks={allTasks}
          categories={categories}
          projects={syncedProjects}
          submissions={submissions}
          onAddTask={handleAddTask}
          onUpdateTask={handleUpdateTask}
          onDeleteTask={handleDeleteTask}
          onAddCategory={handleAddCategory}
          onAddProject={handleAddProject}
          onUpdateProject={handleUpdateProject}
          onDeleteProject={handleDeleteProject}
          onLogout={handleLogout}
        />
      </ErrorBoundary>
    );
  }

  // Filter projects based on user access. '*' means all projects (used by operators).
  const userProjects = accessibleProjects.includes('*')
    ? syncedProjects
    : accessibleProjects.length > 0
      ? syncedProjects.filter(p => accessibleProjects.includes(p.id))
      : syncedProjects;

  // Operator Home Screen
  if (userRoleType === 'operator' && userScreen === 'home') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <OperatorHomeScreen
          operatorEmail={userPersonName || userName}
          onGoTasks={() => setUserScreen('tasks')}
          onGoClock={() => setUserScreen('clock')}
          onGoTimesheets={() => setUserScreen('timesheets')}
          onLogout={handleUserLogout}
        />
      </ErrorBoundary>
    );
  }

  // Operator Clock In/Out Screen
  if (userRoleType === 'operator' && userScreen === 'clock') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <EmployeeTimeTrackingScreen
          employeeName={userPersonName || userName}
          employeeEmail={userRole}
          projects={userProjects}
          activeProjectId={activeProjectId}
          currentEntry={currentEmployeeTimeEntry}
          onSelectProject={setActiveProjectId}
          onClockIn={handleEmployeeClockIn}
          onClockOut={handleEmployeeClockOut}
          onGoHome={() => setUserScreen('home')}
          onLogout={handleUserLogout}
        />
      </ErrorBoundary>
    );
  }

  // Operator Timesheets (own row only)
  if (userRoleType === 'operator' && userScreen === 'timesheets') {
    const myOperator = operators.find(op => op.id === userEmployeeId) ||
      (userEmployeeId ? { id: userEmployeeId, email: userRole, pin: '', isActive: true, regularHoursPerWeek: 40, allFutureProjects: true, projectIds: [] } : null);
    return (
      <ErrorBoundary onReset={handleLogout}>
        <TimesheetsScreen
          operators={myOperator ? [myOperator] : []}
          projects={userProjects}
          onGoHome={() => setUserScreen('home')}
          onLogout={handleUserLogout}
          userName={userPersonName || userName}
          hidePeopleTabs
        />
      </ErrorBoundary>
    );
  }

  // Supervisor Home Screen
  if (userRoleType === 'microsoft' && userScreen === 'home') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <SupervisorHomeScreen
          supervisorName={userPersonName || userName}
          onGoTasks={() => setUserScreen('tasks')}
          onGoClock={() => setUserScreen('clock')}
          onGoTimesheets={() => setUserScreen('timesheets')}
          onLogout={handleUserLogout}
        />
      </ErrorBoundary>
    );
  }

  // Supervisor Clock In/Out Screen
  if (userRoleType === 'microsoft' && userScreen === 'clock') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <EmployeeTimeTrackingScreen
          employeeName={userPersonName || userName}
          employeeEmail={userRole}
          projects={userProjects}
          activeProjectId={activeProjectId}
          currentEntry={currentEmployeeTimeEntry}
          onSelectProject={setActiveProjectId}
          onClockIn={handleEmployeeClockIn}
          onClockOut={handleEmployeeClockOut}
          onGoHome={() => setUserScreen('home')}
          onLogout={handleUserLogout}
        />
      </ErrorBoundary>
    );
  }

  // Supervisor Timesheets (all operators + supervisor tabs)
  if (userRoleType === 'microsoft' && userScreen === 'timesheets') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <TimesheetsScreen
          operators={operators}
          projects={syncedProjects}
          onGoHome={() => setUserScreen('home')}
          onLogout={handleUserLogout}
          userName={userPersonName || userName}
        />
      </ErrorBoundary>
    );
  }

  // Legacy employee flow (backward compat)
  if (userRoleType === 'employee') {
    return (
      <ErrorBoundary onReset={handleLogout}>
        <EmployeeTimeTrackingScreen
          employeeName={userPersonName || userName}
          employeeEmail={userRole}
          projects={userProjects}
          activeProjectId={activeProjectId}
          currentEntry={currentEmployeeTimeEntry}
          onSelectProject={setActiveProjectId}
          onClockIn={handleEmployeeClockIn}
          onClockOut={handleEmployeeClockOut}
          onLogout={handleLogout}
        />
      </ErrorBoundary>
    );
  }

  // Operators/supervisors in tasks mode get a Home button; vendors/others don't
  const handleGoHome = (userRoleType === 'operator' || userRoleType === 'microsoft')
    ? () => { setUserScreen('home'); setSelectedCategory(null); setSelectedTask(null); }
    : undefined;

  // User View — Task Form
  if (selectedTask) {
    const activeProject = syncedProjects.find(p => p.id === activeProjectId);
    const taskData =
      activeProject?.tasks.find(t => t.id === selectedTask) ||
      allTasks.find(t => t.id === selectedTask);
    return (
      <ErrorBoundary onReset={handleBackToCategories}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">
                {taskData ? taskData.title : selectedTask} — Daily Report
              </h1>
              <p className="text-gray-600">Fill in today's progress below</p>
            </div>
            <UserForm
              taskName={selectedTask}
              customTask={taskData}
              submittedBy={userName}
              onComplete={(responses) => handleTaskComplete(selectedTask, responses)}
              onCancel={() => setSelectedTask(null)}
            />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // User View — Task Selection
  if (selectedCategory) {
    const activeProject = syncedProjects.find(p => p.id === activeProjectId);
    
    // Prefer the selected project's task list so users only submit against
    // tasks that are actually part of that project and visible in reports.
    const projectTasks = activeProject?.tasks?.length ? activeProject.tasks : allTasks;
    let filteredTasks = projectTasks.filter(task => task.categoryId === selectedCategory);
    
    // Role-based filtering: Only show tasks assigned to the user's role
    filteredTasks = filteredTasks.filter(task => {
      // If task has no assignedTo property, DON'T show it (must be explicitly assigned)
      if (!task.assignedTo) {
        return false;
      }
      
      // Check if user's role has access to this task
      if (userRoleType === 'operator') {
        return task.assignedTo.operators === true;
      } else if (userRoleType === 'vendor' && userVendorId) {
        return task.assignedTo.vendors?.includes(userVendorId) === true;
      } else if (userRoleType === 'supervisor') {
        const sup = task.assignedTo.supervisors;
        if (typeof sup === 'boolean') return sup;
        return Array.isArray(sup) && sup.includes(userVendorId || '');
      } else if (userRoleType === 'microsoft') {
        const sup = task.assignedTo.supervisors;
        if (typeof sup === 'boolean') return sup;
        return Array.isArray(sup) && sup.includes(userVendorId || '');
      }
      
      // Default: no access
      return false;
    });
    
    const categoryName = categories.find(c => c.id === selectedCategory)?.name || selectedCategory;

    return (
      <ErrorBoundary onReset={handleBackToCategories}>
        <TaskSelection
          completedTasks={completedTasks}
          customTasks={filteredTasks}
          categoryName={categoryName}
          activeProject={activeProject}
          projects={syncedProjects}
          activeProjectId={activeProjectId}
          onSelectTask={setSelectedTask}
          onSelectProject={setActiveProjectId}
          onBack={handleBackToCategories}
          onGoHome={handleGoHome}
          onLogout={(userRoleType === 'operator' || userRoleType === 'microsoft') ? handleUserLogout : handleLogout}
        />
      </ErrorBoundary>
    );
  }

  // User View — Category Selection
  const activeProject = syncedProjects.find(p => p.id === activeProjectId);
  
  // Show categories from the active project (if one is selected)
  let userCategories = categories;
  if (activeProject) {
    userCategories = categories.filter(c => activeProject.categoryIds?.includes(c.id));
  }

  return (
    <ErrorBoundary onReset={handleLogout}>
      <CategorySelection
        categories={userCategories}
        projects={userProjects}
        activeProjectId={activeProjectId}
        onSelectCategory={setSelectedCategory}
        onSelectProject={setActiveProjectId}
        onGoHome={handleGoHome}
        onLogout={(userRoleType === 'operator' || userRoleType === 'microsoft') ? handleUserLogout : handleLogout}
        userRole={userRole}
        userPersonName={userPersonName}
        activeProject={activeProject}
      />
    </ErrorBoundary>
  );
}
